import { DateTime } from '@ksyos/date-time';
import {
    ActionBar,
    Box,
    Button,
    Col,
    Divider,
    DropdownMenu,
    Heading,
    IconButton,
    IconCircle,
    Row,
    Text,
    TextInput,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { EpisodeCategory, GroupMeetingPatientAttendance } from '@common/enums';
import type {
    IGroupMeetingPatientWithRequestInformation,
    IGroupMeetingWithPatients,
} from '@common/interfaces/groupMeeting';
import type { Id } from '@common/utils/id';
import type { ITimeRange } from '@common/validation/callBackMoment';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { ToastType } from '@common/enums';
import { GroupMeetingType } from '@common/enums/groupMeetingType';
import { SortDirection } from '@common/enums/sortDirection';
import { timeRangeValidatorSchema, validateBothInputsFilled, validateTimePeriod } from '@common/validation/timeSlots';
import { BadgeWithLabel } from '@frontend/components/elements/BadgeWithLabel';
import { ListPager } from '@frontend/components/elements/ListPager';
import { NullableFormattedDate } from '@frontend/components/elements/NullableFormattedDate';
import { IndependentDateInput, isValidTime, parseEnteredValue } from '@frontend/components/fields';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    showToast,
    updateValidationErrorsForMany,
} from '@frontend/utils';
import { GroupMeetingPatientsList } from './GroupMeetingPatientsList';
import { StyledGroupMeetingBox } from './StyledGroupMeetingComponents';

interface IServiceEmployment {
    id: string;
    service: {
        episodeCategory: {
            name: EpisodeCategory;
        };
        id: string;
        name: string;
    };
    settings?: {
        canEditGroupSession?: boolean;
        canEditMdo?: boolean;
        canRegisterGroupSessionAttendance?: boolean;
        showPoliPatientDashboard?: boolean;
    };
}

enum PatientSelectionOrigin {
    groupMeeting = 'groupMeeting',
    waitingList = 'waitingList',
}

interface IGroupMeetingBoxFields {
    date?: string | null;
    endTime?: string | null;
    startTime?: string | null;
}

enum GroupMeetingEditRightsKeys {
    bariGroupSession = 'canEditGroupSession',
    bariMdo = 'canEditMdo',
}

const searchableGroupMeetingTypes = [GroupMeetingType.bariGroupSession];

export function GroupMeetingManagement(props: { type: GroupMeetingType }) {
    const [selectedGroupMeetingId, setSelectedGroupMeetingId] = useState<string>();
    const [groupMeetingList, setGroupMeetingList] = useState<IGroupMeetingWithPatients[]>([]);
    const [emptyGroupMeetingExists, setEmptyGroupMeetingExists] = useState<boolean>(false);

    const [patientsWaitingList, setPatientsWaitingList] = useState<IGroupMeetingPatientWithRequestInformation[]>([]);
    const [waitingListLength, setWaitingListLength] = useState<number>(0);
    const [currentWaitingListPage, setCurrentWaitingListPage] = useState<number>(1);
    const [waitingListPageSize, setWaitingListPageSize] = useState<number>(15);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ascending);

    const [selectedGroupMeetingPatientIds, setSelectedGroupMeetingPatientIds] = useState<string[]>([]);
    const [selectedPatientIdsOrigin, setSelectedPatientIdsOrigin] = useState<PatientSelectionOrigin>();
    const [isAddNewEmptyGroupMeetingButtonEnabled, setIsAddNewEmptyGroupMeetingButtonEnabled] = useState<boolean>(true);
    const [dateErrors, setDateErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [timeErrors, setTimeErrors] = useState<ValidationErrors>(emptyValidationErrors);

    const [canEdit, setCanEdit] = useState<boolean>(false);
    const [canRegisterGroupSessionAttendance, setCanRegisterGroupSessionAttendance] = useState<boolean>(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [patientWaitingListUnfilteredCount, setPatientWaitingListUnfilteredCount] = useState(0);

    const editRightsKey: GroupMeetingEditRightsKeys = GroupMeetingEditRightsKeys[props.type];

    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);
    const organizationId = useSelector((state: IReduxState) => state.authentication.organizationId);

    useEffect(() => {
        loadCanEditGroupMeeting();
        loadWaitingListAndGroupMeetingList(false, true);
    }, [props.type]);

    useEffect(() => {
        loadWaitingList(false, true);
    }, [currentWaitingListPage, waitingListPageSize, sortDirection]);

    useEffect(() => {
        if (selectedPatientIdsOrigin === PatientSelectionOrigin.groupMeeting) {
            setSelectedGroupMeetingPatientIds([]);
        }
        setTimeErrors(emptyValidationErrors);
    }, [selectedGroupMeetingId]);

    useEffect(() => {
        const emptyGroupMeetings = groupMeetingList.filter(
            (groupMeeting) => !groupMeeting.date && !groupMeeting.groupMeetingPatients.length
        );
        setEmptyGroupMeetingExists(!!emptyGroupMeetings.length);
    }, [groupMeetingList]);

    useEffect(() => {
        setIsAddNewEmptyGroupMeetingButtonEnabled(canAddNewEmptyGroupMeeting());
    }, [emptyGroupMeetingExists, selectedGroupMeetingId, dateErrors]);

    useEffect(() => {
        if (!searchableGroupMeetingTypes.includes(props.type) || patientWaitingListUnfilteredCount === 0) {
            setSearchTerm('');
        }
    }, [props.type, patientWaitingListUnfilteredCount]);

    return (
        <PageTemplate>
            <Heading
                mb={12}
                mt={10}
                size="xlarge"
            >
                {translateKey(`groupMeetingType.${props.type}`)}
            </Heading>

            <Box
                display="flex"
                flexWrap="wrap"
                gap={4}
                mb={4}
            >
                {groupMeetingList.map((groupMeetingItem) => {
                    return props.type === GroupMeetingType.bariMdo
                        ? renderMdoBox(groupMeetingItem)
                        : renderGroupSessionBox(groupMeetingItem);
                })}

                {canEdit && renderAddNewGroupMeetingButton()}
            </Box>

            {renderCurrentGroupMeetingPatients()}

            <Divider />

            <Heading
                mb={3}
                size="medium"
            >
                {props.type === GroupMeetingType.bariGroupSession
                    ? 'Patiënten klaar voor de groepsvoorlichting'
                    : 'Wachtlijst'}
            </Heading>

            <GroupMeetingPatientsList
                groupMeetingType={props.type}
                handleChangeSortDirection={() =>
                    setSortDirection(
                        sortDirection === SortDirection.ascending ? SortDirection.descending : SortDirection.ascending
                    )
                }
                handleSelect={(id) => handlePatientSelect(id, PatientSelectionOrigin.waitingList)}
                isSortable={true}
                patients={patientsWaitingList}
                searchBarProps={{
                    onSearch: async (searchText) => await loadWaitingList(true, false, searchText),
                    onSearchTermChange: setSearchTerm,
                    searchTerm,
                    showSearchBar:
                        searchableGroupMeetingTypes.includes(props.type) && patientWaitingListUnfilteredCount > 0,
                }}
                selectedPatients={selectedGroupMeetingPatientIds}
                showSelectRowCheckboxes={canEdit}
                sortDirection={sortDirection}
            />

            <ListPager
                isDisabled={false}
                itemsPerPage={waitingListPageSize}
                onChangeItemsPerPage={handleChangePageSize}
                onChangePage={async (value) => setCurrentWaitingListPage(value)}
                page={currentWaitingListPage}
                total={waitingListLength}
            />

            <ActionBar
                isOpen={!!selectedGroupMeetingPatientIds.length}
                onClose={() => {
                    setSelectedGroupMeetingPatientIds([]);
                    setSelectedPatientIdsOrigin(undefined);
                }}
            >
                <Box
                    alignItems="center"
                    display="flex"
                    gap={3}
                    justifyContent="space-between"
                >
                    <BadgeWithLabel
                        color="inverse"
                        label={translateKey('common.isSelected')}
                        value={selectedGroupMeetingPatientIds.length.toString()}
                    />
                    {selectedPatientIdsOrigin === PatientSelectionOrigin.waitingList && (
                        <Button
                            onClick={async () => {
                                await handleGroupMeetingPatientsChange(handlePatientListChange);
                            }}
                            variant="solid"
                        >
                            Voeg toe aan {props.type === GroupMeetingType.bariMdo ? 'MDO' : 'Groepsvoorlichting'}
                        </Button>
                    )}

                    {selectedPatientIdsOrigin === PatientSelectionOrigin.groupMeeting &&
                        props.type === GroupMeetingType.bariMdo && (
                            <DropdownMenu
                                trigger={
                                    <Button
                                        iconRight="caret-up"
                                        variant="solid"
                                    >
                                        Verplaats naar
                                    </Button>
                                }
                            >
                                <DropdownMenu.Item
                                    onClick={async () => {
                                        await handleGroupMeetingPatientsChange(handleMoveToNextGroupMeeting);
                                    }}
                                >
                                    Volgende MDO
                                </DropdownMenu.Item>
                                <DropdownMenu.Item
                                    onClick={async () => {
                                        await handleGroupMeetingPatientsChange(handlePatientListChange);
                                    }}
                                >
                                    Terug op de wachtlijst
                                </DropdownMenu.Item>
                            </DropdownMenu>
                        )}
                    {selectedPatientIdsOrigin === PatientSelectionOrigin.groupMeeting &&
                        props.type === GroupMeetingType.bariGroupSession && (
                            <Button
                                onClick={async () => {
                                    await handleGroupMeetingPatientsChange(handlePatientListChange);
                                }}
                                variant="solid"
                            >
                                Zet terug op de wachtlijst
                            </Button>
                        )}
                </Box>
            </ActionBar>
        </PageTemplate>
    );

    function renderGroupSessionBox(groupSessionItem: IGroupMeetingWithPatients) {
        const isGroupSessionSelected = selectedGroupMeetingId === groupSessionItem.id;

        return (
            <StyledGroupMeetingBox
                key={groupSessionItem.id}
                onClick={async () => {
                    if (selectedGroupMeetingId !== groupSessionItem.id && canEdit) {
                        setTimeErrors(emptyValidationErrors);
                        const previouslySelectedGroupMeeting = groupMeetingList.find(
                            (groupMeeting) => groupMeeting.id === selectedGroupMeetingId
                        );
                        await validateAndHandleTimeSlotChange(
                            'startTime',
                            previouslySelectedGroupMeeting?.startTime ?? undefined,
                            previouslySelectedGroupMeeting!,
                            true
                        );
                    }
                    await handleChangeSelectedGroupMeetingId(groupSessionItem.id);
                }}
                style={{
                    border: isGroupSessionSelected ? '1px solid #7090e5' : undefined,
                    borderRadius: isGroupSessionSelected ? '4px' : undefined,
                    outline: isGroupSessionSelected ? '4px solid #ebf0fd' : undefined,
                    paddingTop:
                        !groupSessionItem.date && groupSessionItem.groupMeetingPatients.length
                            ? '62px'
                            : canDeleteGroupMeeting(groupSessionItem)
                              ? '30px'
                              : '22px',
                }}
                sx={{
                    '&:hover': {
                        border: '1px solid #a69a91',
                    },
                }}
            >
                {canEdit && (isGroupSessionSelected || !groupSessionItem.date)
                    ? renderGroupSessionBoxAddEditFields(groupSessionItem, isGroupSessionSelected)
                    : renderGroupSessionBoxReadFields(groupSessionItem)}

                <Text
                    color="minimal"
                    mt={4}
                    size="sm"
                    textAlign="right"
                >
                    {groupSessionItem.patientsTotalCount} patiënten
                </Text>
            </StyledGroupMeetingBox>
        );
    }

    function renderGroupSessionBoxAddEditFields(
        groupSessionItem: IGroupMeetingWithPatients,
        isGroupSessionSelected: boolean
    ) {
        return (
            <>
                {canDeleteGroupMeeting(groupSessionItem) && (
                    <IconButton
                        icon="close"
                        onClick={async () => await deleteGroupMeeting(groupSessionItem.id)}
                        size="small"
                        style={{
                            bottom: '21px',
                            left: '164px',
                            position: 'relative',
                        }}
                    />
                )}
                {groupSessionItem.date && DateTime.from(groupSessionItem.date).isValid() && (
                    <p>
                        {translateKey(`daysEnum.${DateTime.from(groupSessionItem.date).locale('en').format('dddd')}`)}
                    </p>
                )}
                <IndependentDateInput
                    error={isGroupSessionSelected ? dateErrors.get(groupSessionItem.id) : ''}
                    minDate={DateTime.now().format()}
                    onChange={async (value) => await validateAndHandleGroupMeetingDateChange(value ?? undefined)}
                    placeholder={translateKey('common.datePlaceholder')}
                    size="large"
                    value={groupSessionItem.date ? groupSessionItem.date.toString() : null}
                    variant="outline"
                />
                {renderTimeSlot(groupSessionItem, false)}
            </>
        );
    }

    function renderTimeSlot(groupSessionItem: IGroupMeetingWithPatients, isReadOnly: boolean) {
        return (
            <Row
                className="mt-2"
                columnGap={1}
            >
                {renderTimeField(groupSessionItem, 'startTime', isReadOnly)}
                <Col span={2}>
                    <Text
                        as="small"
                        fontWeight="bold"
                        mt={2}
                        size="md"
                        textAlign="center"
                    >
                        {'-'}
                    </Text>
                </Col>
                {renderTimeField(groupSessionItem, 'endTime', isReadOnly)}
            </Row>
        );
    }

    function renderTimeField(
        groupSessionItem: IGroupMeetingWithPatients,
        timeField: keyof ITimeRange,
        isReadOnly: boolean
    ) {
        if (isReadOnly) {
            return (
                <Col span={5}>
                    <Text
                        fontWeight="medium"
                        mt={2}
                    >
                        {groupSessionItem[timeField] ?? ''}
                    </Text>
                </Col>
            );
        }

        return (
            <Col span={5}>
                <TextInput
                    error={
                        timeField === 'startTime'
                            ? getValidationErrorTranslation(timeErrors.get(groupSessionItem.id))
                            : ''
                    }
                    onBlur={async () => {
                        await validateAndHandleTimeFieldChange(groupSessionItem, timeField);
                    }}
                    onChange={(e) => {
                        const updatedGroupSession = { ...groupSessionItem, [timeField]: e.target.value };
                        setGroupMeetingList(
                            groupMeetingList.map((groupMeeting) =>
                                groupMeeting.id === selectedGroupMeetingId ? updatedGroupSession : groupMeeting
                            )
                        );
                    }}
                    onEnterKeyPress={async () => {
                        await validateAndHandleTimeFieldChange(groupSessionItem, timeField);
                    }}
                    placeholder={!isReadOnly ? (timeField === 'startTime' ? '09:30' : '10:30') : ''}
                    style={{
                        padding: '6px',
                        paddingLeft: timeField === 'startTime' ? '10px' : '6px',
                    }}
                    type="text"
                    value={groupSessionItem[timeField] ?? ''}
                    variant="outline"
                />
            </Col>
        );
    }

    function renderGroupSessionBoxReadFields(groupSessionItem: IGroupMeetingWithPatients) {
        return (
            <>
                <p>
                    {translateKey(
                        `daysEnum.${DateTime.from(groupSessionItem.date ?? new Date())
                            .locale('en')
                            .format('dddd')}`
                    )}
                </p>
                <Text
                    color={groupSessionItem.date ? 'default' : 'minimal'}
                    fontWeight={groupSessionItem.date ? 'bold' : undefined}
                    mb={4}
                    ml={3}
                    mt={6}
                >
                    {groupSessionItem.date ? (
                        <NullableFormattedDate value={groupSessionItem.date} />
                    ) : (
                        translateKey('common.datePlaceholder')
                    )}
                </Text>

                {renderTimeSlot(groupSessionItem, true)}
            </>
        );
    }

    function renderMdoBox(mdoItem: IGroupMeetingWithPatients) {
        const isMdoSelected = selectedGroupMeetingId === mdoItem.id;

        return (
            <StyledGroupMeetingBox
                key={mdoItem.id}
                onClick={async () => await handleChangeSelectedGroupMeetingId(mdoItem.id)}
                style={{
                    border: isMdoSelected ? '1px solid #7090e5' : undefined,
                    borderRadius: isMdoSelected ? '4px' : undefined,
                    outline: isMdoSelected ? '4px solid #ebf0fd' : undefined,
                    paddingTop:
                        canEdit && !canDeleteGroupMeeting(mdoItem) && (!mdoItem.date || isMdoSelected)
                            ? '42px'
                            : undefined,
                }}
                sx={{
                    '&:hover': {
                        border: '1px solid #a69a91',
                    },
                }}
            >
                {canEdit && (isMdoSelected || !mdoItem.date)
                    ? renderMdoBoxAddEditFields(mdoItem, isMdoSelected)
                    : renderMdoBoxRead(mdoItem.date)}

                <Text
                    color="minimal"
                    mt={canEdit && (isMdoSelected || !mdoItem.date) ? 4 : 6}
                    size="sm"
                    textAlign="right"
                >
                    {mdoItem.patientsTotalCount} patiënten
                </Text>
            </StyledGroupMeetingBox>
        );
    }

    function renderMdoBoxAddEditFields(mdoItem: IGroupMeetingWithPatients, isMdoSelected: boolean) {
        return (
            <>
                {canDeleteGroupMeeting(mdoItem) && (
                    <IconButton
                        icon="close"
                        onClick={async () => await deleteGroupMeeting(mdoItem.id)}
                        size="small"
                        style={{
                            bottom: '8px',
                            left: '164px',
                            position: 'relative',
                        }}
                    />
                )}
                <IndependentDateInput
                    error={isMdoSelected ? dateErrors.get(mdoItem.id) : ''}
                    minDate={DateTime.now().format()}
                    onChange={async (value) => await validateAndHandleGroupMeetingDateChange(value ?? undefined)}
                    placeholder={translateKey('common.datePlaceholder')}
                    size="large"
                    value={mdoItem.date ? mdoItem.date.toString() : null}
                    variant="outline"
                />
            </>
        );
    }

    function renderMdoBoxRead(mdoItemDate: Date | string | null) {
        return (
            <Text
                color={mdoItemDate ? 'default' : 'minimal'}
                fontWeight={mdoItemDate ? 'bold' : undefined}
                mb={5}
                ml={4}
                mt={10}
                size="md"
            >
                {mdoItemDate ? <NullableFormattedDate value={mdoItemDate} /> : translateKey('common.datePlaceholder')}
            </Text>
        );
    }

    function renderAddNewGroupMeetingButton() {
        return (
            <StyledGroupMeetingBox
                onClick={async () => {
                    if (isAddNewEmptyGroupMeetingButtonEnabled) {
                        await createGroupMeeting();
                    }
                }}
                style={{
                    alignItems: 'center',
                    background: isAddNewEmptyGroupMeetingButtonEnabled ? '' : '#F4F4F2',
                    display: 'flex',
                }}
                sx={
                    isAddNewEmptyGroupMeetingButtonEnabled && {
                        '&:hover': {
                            border: '1px solid #a69a91',
                        },
                    }
                }
            >
                <>
                    <IconCircle
                        hasBorder={true}
                        icon="add"
                        style={{
                            borderColor: isAddNewEmptyGroupMeetingButtonEnabled ? 'blue' : '#DBD5D0',
                            color: isAddNewEmptyGroupMeetingButtonEnabled ? 'blue' : '#DBD5D0',
                            margin: 'auto',
                        }}
                    />
                </>
            </StyledGroupMeetingBox>
        );
    }

    function renderCurrentGroupMeetingPatients() {
        const selectedGroupMeeting = groupMeetingList.find(
            (groupMeeting) => groupMeeting.id === selectedGroupMeetingId
        );

        if (selectedGroupMeeting?.groupMeetingPatients?.length) {
            return (
                <GroupMeetingPatientsList
                    groupMeeting={selectedGroupMeeting}
                    groupMeetingType={props.type}
                    handleCloseGroupMeeting={handleCloseGroupMeeting}
                    handleSelect={(id) => handlePatientSelect(id, PatientSelectionOrigin.groupMeeting)}
                    isClosable={true}
                    patients={selectedGroupMeeting.groupMeetingPatients}
                    reload={async () => {
                        setDateErrors(emptyValidationErrors);
                        await loadWaitingListAndGroupMeetingList();
                    }}
                    selectedPatients={selectedGroupMeetingPatientIds}
                    showRegisterGroupSessionAttendanceButton={
                        props.type === GroupMeetingType.bariGroupSession && canRegisterGroupSessionAttendance
                    }
                    showSelectRowCheckboxes={canEdit}
                    submitAttendances={submitAttendances}
                />
            );
        }

        return (
            <div
                className="empty-group-meeting-appointment"
                style={{ marginTop: 50 }}
            >
                <div className="__text-body">
                    <Text size="sm">
                        Selecteer patiënten in de wachtlijst voor{' '}
                        {props.type === GroupMeetingType.bariMdo ? 'het MDO' : 'de groepsvoorlichting'}
                    </Text>
                </div>
            </div>
        );
    }

    async function validateAndHandleTimeFieldChange(
        groupSessionItem: IGroupMeetingWithPatients,
        timeFieldName: keyof ITimeRange
    ) {
        const notSelectedTimeField = timeFieldName === 'startTime' ? 'endTime' : 'startTime';
        const parsedTimeField = parseEnteredValue(groupSessionItem[timeFieldName] ?? '');

        if (parsedTimeField !== groupSessionItem[timeFieldName]) {
            updateGroupMeetingFromList(selectedGroupMeetingId, {
                [timeFieldName]: parsedTimeField,
            });
        }

        if (isValidTime(parsedTimeField)) {
            if (groupSessionItem[notSelectedTimeField]) {
                await validateAndHandleTimeSlotChange(timeFieldName, parsedTimeField, groupSessionItem);
            }
        } else {
            updateGroupMeetingFromList(selectedGroupMeetingId, {
                [timeFieldName]: null,
            });
        }
    }

    function isDateInThePast(date: string) {
        const today = new Date();

        return DateTime.from(today).isAfter(date, 'days');
    }

    function getDateValidationError(newDate?: string) {
        if (newDate) {
            if (!DateTime.from(newDate).isValid()) {
                return 'koi_date';
            }
            if (isDateInThePast(newDate)) {
                return 'date_past';
            }
            if (DateTime.from(newDate).isWeekend()) {
                return 'notValidWeekend';
            }
        }
    }

    async function validateAndHandleGroupMeetingDateChange(newDate?: string) {
        const dateError = getDateValidationError(newDate);
        const newErrors = dateErrors.set(selectedGroupMeetingId!, dateError ?? undefined);

        setDateErrors(newErrors);

        if (!dateError) {
            await editSelectedGroupMeetingBoxFields({ date: newDate ?? null });
        }

        const updatedGroupMeetingListWithNewDate = groupMeetingList.map((groupMeeting) =>
            groupMeeting.id === selectedGroupMeetingId
                ? {
                      ...groupMeeting,
                      date: newDate?.toLowerCase() === 'invalid date' || newDate === undefined ? null : newDate,
                  }
                : groupMeeting
        );

        setGroupMeetingList(updatedGroupMeetingListWithNewDate);
        const emptyGroupMeetings = groupMeetingList.filter(
            (groupMeeting) => !groupMeeting.date && !groupMeeting.groupMeetingPatients.length
        );
        setEmptyGroupMeetingExists(!!emptyGroupMeetings.length);
    }

    async function validateAndHandleTimeSlotChange(
        field: keyof ITimeRange,
        value: string | undefined,
        groupMeetingToUpdate: IGroupMeetingWithPatients,
        resetTimeSlotFieldsIfOneIsEmpty?: boolean
    ) {
        const parsedTimeSlot = {
            endTime:
                field === 'endTime' && value
                    ? parseEnteredValue(value)
                    : (groupMeetingToUpdate.endTime?.toString() ?? null),
            startTime:
                field === 'startTime' && value
                    ? parseEnteredValue(value)
                    : (groupMeetingToUpdate.startTime?.toString() ?? null),
        };

        const timeSchemaValidationErrors = updateValidationErrorsForMany(
            timeRangeValidatorSchema,
            parsedTimeSlot,
            emptyValidationErrors,
            ['timeSlot.startTime', 'timeSlot.endTime']
        );

        let timeSlotErrors = validateBothInputsFilled(parsedTimeSlot, timeSchemaValidationErrors, 'timeSlot.startDate');

        const timePeriodErrors = validateTimePeriod(parsedTimeSlot, emptyValidationErrors, groupMeetingToUpdate.id);

        setTimeErrors(
            timePeriodErrors
                ? timeErrors.set(groupMeetingToUpdate.id, timePeriodErrors.get(groupMeetingToUpdate.id))
                : emptyValidationErrors
        );
        timeSlotErrors = timeSlotErrors.merge(timePeriodErrors);

        if (timeSlotErrors.isEmpty() && timePeriodErrors.isEmpty()) {
            await editSelectedGroupMeetingBoxFields(parsedTimeSlot, groupMeetingToUpdate.id);
        } else if (!timeSlotErrors.isEmpty() && resetTimeSlotFieldsIfOneIsEmpty) {
            await editSelectedGroupMeetingBoxFields({ endTime: null, startTime: null }, groupMeetingToUpdate.id);
            updateGroupMeetingFromList(selectedGroupMeetingId, {
                endTime: null,
                startTime: null,
            });
        } else if (!timePeriodErrors.isEmpty()) {
            await editSelectedGroupMeetingBoxFields({ endTime: null, startTime: null }, groupMeetingToUpdate.id);
            updateGroupMeetingFromList(selectedGroupMeetingId, {
                endTime: parsedTimeSlot.endTime,
                startTime: parsedTimeSlot.startTime,
            });
        } else {
            updateGroupMeetingFromList(selectedGroupMeetingId, {
                endTime: parsedTimeSlot.endTime,
                startTime: parsedTimeSlot.startTime,
            });
        }
    }

    function updateGroupMeetingFromList(
        groupMeetingId: string | undefined,
        fieldsToUpdate: Partial<IGroupMeetingWithPatients>
    ) {
        setGroupMeetingList(
            groupMeetingList.map((groupMeeting) =>
                groupMeeting.id === groupMeetingId
                    ? {
                          ...groupMeeting,
                          ...fieldsToUpdate,
                      }
                    : groupMeeting
            )
        );
    }

    function handlePatientSelect(groupMeetingPatient: string, origin: PatientSelectionOrigin) {
        if (selectedGroupMeetingPatientIds.includes(groupMeetingPatient)) {
            setSelectedGroupMeetingPatientIds(
                selectedGroupMeetingPatientIds.filter((id) => id !== groupMeetingPatient)
            );
        } else {
            let newSelectedGroupMeetingPatientIds = selectedGroupMeetingPatientIds;
            if (selectedPatientIdsOrigin !== origin) {
                newSelectedGroupMeetingPatientIds = [];
                setSelectedPatientIdsOrigin(origin);
            }
            setSelectedGroupMeetingPatientIds([...newSelectedGroupMeetingPatientIds, groupMeetingPatient]);
        }
    }

    async function handleGroupMeetingPatientsChange(groupMeetingAction: () => Promise<void>) {
        if (!dateErrors.get(selectedGroupMeetingId!)) {
            await groupMeetingAction();
        } else {
            setSelectedGroupMeetingPatientIds([]);
            setSelectedPatientIdsOrigin(undefined);
        }
    }

    async function handlePatientListChange() {
        let resetPagingAfterLoad = false;

        const selectedGroupMeeting = groupMeetingList.find(
            (groupMeeting) => groupMeeting.id === selectedGroupMeetingId
        );

        if (!selectedGroupMeeting || !isGroupMeetingCreated(selectedGroupMeeting.id)) {
            await createGroupMeeting(selectedGroupMeeting?.id, selectedGroupMeetingPatientIds);
        } else {
            if (selectedGroupMeetingId) {
                const willAnyPatientBeDuplicated = await isAnyPatientDuplicated(selectedGroupMeetingId);
                if (willAnyPatientBeDuplicated) {
                    showToast(ToastType.warning, translateKey('toast.duplicatedMdoPatient'));

                    return;
                }
            }

            let url: string;

            if (selectedPatientIdsOrigin === 'waitingList') {
                resetPagingAfterLoad = true;
                url = '/api/groupMeeting/addPatients';
            } else {
                url = '/api/groupMeeting/removePatients';
            }

            await fetchJson(url, 'POST', {
                groupMeetingId: selectedGroupMeetingId,
                groupMeetingPatientIds: selectedGroupMeetingPatientIds,
                type: props.type,
            });
            if (props.type === GroupMeetingType.bariGroupSession) {
                await validateAndHandleTimeSlotChange(
                    'startTime',
                    selectedGroupMeeting?.startTime ?? undefined,
                    selectedGroupMeeting!,
                    true
                );
                setTimeErrors(emptyValidationErrors);
            }

            await loadWaitingListAndGroupMeetingList(resetPagingAfterLoad, true);
        }
    }

    async function handleMoveToNextGroupMeeting() {
        const nextGroupMeetingIndex =
            groupMeetingList.findIndex((groupMeeting) => groupMeeting.id === selectedGroupMeetingId) + 1;

        let nextGroupMeetingId;
        if (nextGroupMeetingIndex >= groupMeetingList.length) {
            nextGroupMeetingId = await createGroupMeeting(groupMeetingList[nextGroupMeetingIndex]?.id);
        } else {
            nextGroupMeetingId = groupMeetingList[nextGroupMeetingIndex].id;
        }

        const willAnyPatientBeDuplicated = await isAnyPatientDuplicated(nextGroupMeetingId, selectedGroupMeetingId);
        if (willAnyPatientBeDuplicated) {
            showToast(ToastType.warning, translateKey('toast.duplicatedMdoPatient'));

            return;
        }

        await fetchJson('/api/groupMeeting/removePatients', 'POST', {
            groupMeetingId: selectedGroupMeetingId,
            groupMeetingPatientIds: selectedGroupMeetingPatientIds,
            type: props.type,
        });

        await fetchJson('/api/groupMeeting/addPatients', 'POST', {
            groupMeetingId: nextGroupMeetingId,
            groupMeetingPatientIds: selectedGroupMeetingPatientIds,
            type: props.type,
        });

        await loadWaitingListAndGroupMeetingList(true);
    }

    async function editSelectedGroupMeetingBoxFields(
        fields: IGroupMeetingBoxFields,
        previousSelectedGroupMeeting?: string
    ) {
        return await fetchJson('/api/groupMeeting', 'POST', {
            groupMeetingId: previousSelectedGroupMeeting ?? selectedGroupMeetingId,
            ...fields,
        });
    }

    function isGroupMeetingCreated(id?: string) {
        return !!groupMeetingList.find((groupMeeting) => groupMeeting.id === id);
    }

    async function createGroupMeeting(id?: string, groupMeetingPatientIds?: string[]) {
        if (!groupMeetingPatientIds) {
            setIsAddNewEmptyGroupMeetingButtonEnabled(false);
        }
        const groupMeetingId = id ?? uuidv4();

        const result = await fetchJson('/api/groupMeeting', 'POST', {
            groupMeetingId,
            groupMeetingPatientIds,
            organizationId,
            type: props.type,
        });

        setGroupMeetingList(groupMeetingList.filter((groupMeeting) => groupMeeting.id !== id));

        await loadWaitingListAndGroupMeetingList(true, true);

        setSelectedGroupMeetingId(groupMeetingId);

        return result.json.id;
    }

    async function deleteGroupMeeting(groupMeetingId: string) {
        await fetchJson('/api/groupMeeting', 'DELETE', { id: groupMeetingId, type: props.type });
        await loadWaitingListAndGroupMeetingList(false, true);
    }

    async function loadWaitingListAndGroupMeetingList(resetPaging?: boolean, waitingListChanged?: boolean) {
        await Promise.all([loadWaitingList(resetPaging, waitingListChanged), loadGroupMeetingList()]);
        setSelectedGroupMeetingPatientIds([]);
        setSelectedPatientIdsOrigin(undefined);
    }

    async function loadCanEditGroupMeeting() {
        const serviceEmployments: IServiceEmployment[] = (
            await fetchJson(
                `/api/serviceEmployments?employmentId=${selectedEmploymentId}&includeSettings=true&isActive=true`,
                'GET'
            )
        ).json.items;

        setCanEdit(serviceEmployments.some((serviceEmployment) => serviceEmployment.settings?.[editRightsKey]));

        setCanRegisterGroupSessionAttendance(
            serviceEmployments.some(
                (serviceEmployment) => serviceEmployment.settings?.canRegisterGroupSessionAttendance
            )
        );
    }

    async function loadWaitingList(resetPaging?: boolean, waitingListChanged?: boolean, searchText?: string) {
        let page = currentWaitingListPage;

        if (resetPaging) {
            page = 1;
            setCurrentWaitingListPage(page);
        }

        let url = `/api/groupMeetingPatient/waitingList?limit=${waitingListPageSize}&page=${page}&direction=${sortDirection}&type=${props.type}`;

        if (waitingListChanged) {
            setSearchTerm(''); // reset search term and do not search
        } else {
            const searchQuery = searchText ?? searchTerm;

            if (searchQuery) {
                url += `&searchTerm=${searchQuery}`;
            }
        }

        const result = await fetchJson(url, 'GET');
        const total = result.json.total;

        setPatientsWaitingList(result.json.items);
        setWaitingListLength(total);

        if (waitingListChanged) {
            setPatientWaitingListUnfilteredCount(total);
        }
    }

    async function loadGroupMeetingList(updatedSelectedGroupMeetingId?: string) {
        const result = await fetchJson<{ groupMeetings: IGroupMeetingWithPatients[] }>(
            `/api/groupMeeting?type=${props.type}`,
            'GET'
        );
        const groupMeetings: IGroupMeetingWithPatients[] = result.json?.groupMeetings ?? [];
        const newSelectedGroupMeetingId = updatedSelectedGroupMeetingId ?? selectedGroupMeetingId;

        if (groupMeetings.length) {
            if (!groupMeetings.some((groupMeeting) => groupMeeting.id === newSelectedGroupMeetingId)) {
                setSelectedGroupMeetingId(groupMeetings[0].id);
            } else {
                setSelectedGroupMeetingId(newSelectedGroupMeetingId);
            }
        } else {
            setSelectedGroupMeetingId(undefined);
        }
        setGroupMeetingList(groupMeetings);
    }

    function handleChangePageSize(value: number) {
        setWaitingListPageSize(value);
        if (value * (currentWaitingListPage - 1) > waitingListLength) {
            loadWaitingList(true);
        }
    }

    async function handleCloseGroupMeeting() {
        await fetchJson('/api/groupMeeting/close', 'POST', { id: selectedGroupMeetingId, type: props.type });

        showToast(
            ToastType.success,
            translateKey(
                props.type === GroupMeetingType.bariMdo
                    ? 'bariMdoGroupMeeting.closeMdoSuccessMessage'
                    : 'bariGroupSessionGroupMeeting.closeGroupSessionSuccessMessage'
            )
        );
        await loadWaitingListAndGroupMeetingList();
    }

    async function handleChangeSelectedGroupMeetingId(newSelectedGroupMeetingId: string) {
        if (dateErrors.get(selectedGroupMeetingId!)) {
            await validateAndHandleGroupMeetingDateChange();
        }

        setSelectedGroupMeetingId(newSelectedGroupMeetingId);
    }

    function canAddNewEmptyGroupMeeting() {
        return !emptyGroupMeetingExists && !(selectedGroupMeetingId && dateErrors.get(selectedGroupMeetingId!));
    }

    function canDeleteGroupMeeting(groupMeetingItem: IGroupMeetingWithPatients) {
        return !groupMeetingItem.date && !groupMeetingItem.groupMeetingPatients.length;
    }

    async function isAnyPatientDuplicated(destinationGroupMeetingId: Id, originGroupMeetingId?: Id) {
        let originPatientList: IGroupMeetingPatientWithRequestInformation[];
        if (originGroupMeetingId) {
            originPatientList = groupMeetingList.find(
                (groupMeeting) => groupMeeting.id === originGroupMeetingId
            )!.groupMeetingPatients;
        } else {
            originPatientList = patientsWaitingList;
        }

        const selectedPatientIds = originPatientList
            .filter((groupMeetingPatient) => selectedGroupMeetingPatientIds.includes(groupMeetingPatient.id))
            .map((groupMeetingPatient) => groupMeetingPatient.process.patient.id);

        const destinationGroupMeeting = groupMeetingList.find(
            (groupMeeting) => groupMeeting.id === destinationGroupMeetingId
        );

        return !!destinationGroupMeeting?.groupMeetingPatients.find((groupMeetingPatient) =>
            selectedPatientIds?.includes(groupMeetingPatient.process.patient.id)
        );
    }

    async function submitAttendances(attendances: { [key: string]: GroupMeetingPatientAttendance }) {
        await fetchJson('/api/groupMeetingPatientAttendance', 'POST', {
            attendances,
            groupMeetingId: selectedGroupMeetingId,
        });
    }
}
